/**
 * Container Validation API Hooks
 *
 * React Query hook for fetching validation overview (cross-check + single-doc issues).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '@/lib/api-client';
import { CONTAINER_ISSUES_QUERY_KEYS } from './use-container-issues';

// ============================================================================
// Types
// ============================================================================

export interface ValidationSummary {
    passed: number;
    failed: number;
    skipped: number;
    missing_documents: number;
    total: number;
    single_doc_open: number;
    // Mini summary fields
    missing_required_docs: number;
    missing_required_doc_types: string[];
    single_checks_passed: number;
    single_checks_total: number;
    cross_checks_passed: number;
    cross_checks_total: number;
}

export interface CrossCheckResultDetail {
    result_id: string;
    rule_name: string | null;
    rule_code: string | null;
    comparison_type: string | null;
    status: string; // success | failed | skipped | missing_document | error
    is_valid: boolean | null;
    severity: string | null;
    is_blocking: boolean;
    source_document_id: string | null;
    source_document_name: string | null;
    source_document_type: string | null;
    target_document_id: string | null;
    target_document_name: string | null;
    target_document_type: string | null;
    source_value: string | null;
    target_value: string | null;
    message: string;
    confidence: number | null;
    details: Record<string, unknown> | null;
    executed_at: string;
    issue_id: string | null;
}

export interface SingleDocIssueDetail {
    issue_id: string;
    document_id: string | null;
    document_name: string | null;
    document_type: string | null;
    issue_type: string;
    severity: string;
    issue_description: string;
    affected_field: string | null;
    expected_value: string | null;
    actual_value: string | null;
    detected_by: string;
    resolution_status: string;
    detected_at: string;
}

export interface ContainerValidationOverview {
    container_id: string;
    last_validation_at: string | null;
    summary: ValidationSummary;
    cross_check_results: CrossCheckResultDetail[];
    single_doc_issues: SingleDocIssueDetail[];
    ai_observations: string | null;
}

// ============================================================================
// Query Keys
// ============================================================================

export const CONTAINER_VALIDATION_QUERY_KEYS = {
    all: ['container-validation'] as const,
    overview: (containerId: string) => [...CONTAINER_VALIDATION_QUERY_KEYS.all, 'overview', containerId] as const,
};

// ============================================================================
// Hook
// ============================================================================

export function useContainerValidation(
    containerId: string | undefined,
    options?: { enabled?: boolean }
) {
    return useQuery({
        queryKey: CONTAINER_VALIDATION_QUERY_KEYS.overview(containerId || ''),
        queryFn: async (): Promise<ContainerValidationOverview> => {
            if (!containerId) {
                return {
                    container_id: '',
                    last_validation_at: null,
                    summary: { passed: 0, failed: 0, skipped: 0, missing_documents: 0, total: 0, single_doc_open: 0, missing_required_docs: 0, missing_required_doc_types: [], single_checks_passed: 0, single_checks_total: 0, cross_checks_passed: 0, cross_checks_total: 0 },
                    cross_check_results: [],
                    single_doc_issues: [],
                    ai_observations: null,
                };
            }
            return await apiGet<ContainerValidationOverview>(
                `/validation/containers/${containerId}/overview`
            );
        },
        enabled: (options?.enabled ?? true) && !!containerId,
        staleTime: 30_000,
        placeholderData: (previousData) => previousData,
    });
}

// ============================================================================
// Mutation — Document Issue Status Change
// ============================================================================

interface UpdateDocumentIssueStatusParams {
    issueId: string;
    containerId: string;
    resolution_status: string;
    status_change_reason?: string;
}

export function useUpdateDocumentIssueStatus() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: UpdateDocumentIssueStatusParams) => {
            return await apiPatch<{ issue_id: string; resolution_status: string; synced: boolean }>(
                `/validation/issues/${params.issueId}/status`,
                {
                    resolution_status: params.resolution_status,
                    status_change_reason: params.status_change_reason,
                },
            );
        },
        onSuccess: (_data, variables) => {
            // Invalidate both validation and container issues queries
            queryClient.invalidateQueries({
                queryKey: CONTAINER_VALIDATION_QUERY_KEYS.overview(variables.containerId),
            });
            queryClient.invalidateQueries({
                queryKey: CONTAINER_ISSUES_QUERY_KEYS.list(variables.containerId),
            });
            queryClient.invalidateQueries({ queryKey: ['activities'] });
        },
    });
}

// ============================================================================
// Mutation — Trigger Single Check
// ============================================================================

export interface SingleCheckResult {
    issues_created: number;
    issues_resolved: number;
    missing_types: string[];
    field_issues: Array<{
        document_id: string;
        status: string;
        missing_critical: string[];
        missing_warning: string[];
    }>;
}

export function useTriggerSingleCheck() {
    return useMutation({
        mutationFn: async (containerId: string): Promise<SingleCheckResult> => {
            return await apiPost<SingleCheckResult>(
                `/validation/trigger-single-check/${containerId}`,
                {},
            );
        },
    });
}

// ============================================================================
// Mutation — Run Cross-Checks
// ============================================================================

export interface CrossCheckRunResult {
    expedition_id: string;
    total_rules_executed: number;
    total_validations: number;
    passed_validations: number;
    failed_validations: number;
    blocking_failures: number;
    is_shipment_blocked: boolean;
    issues_created: number;
    validation_results: Array<{
        rule_name: string | null;
        rule_code: string | null;
        status: string;
        is_valid: boolean | null;
        severity: string | null;
        message: string;
        source_document_id: string | null;
        target_document_id: string | null;
    }>;
}

export function useRunCrossChecks() {
    return useMutation({
        mutationFn: async (params: {
            expedition_id: string;
            document_ids: string[];
        }): Promise<CrossCheckRunResult> => {
            return await apiPost<CrossCheckRunResult>('/validation/cross-checks', {
                expedition_id: params.expedition_id,
                document_ids: params.document_ids,
            });
        },
    });
}

// ============================================================================
// Mutation — AI Cross-Analysis
// ============================================================================

export interface AICrossAnalysisFinding {
    severity: string;
    category: string;
    description: string;
    documents_involved: string[];
}

export interface AICrossAnalysisResult {
    findings: AICrossAnalysisFinding[];
    summary: string;
    documents_analyzed: number;
    issues_created: number;
}

export function useAICrossAnalysis() {
    return useMutation({
        mutationFn: async (params: {
            container_id: string;
            document_ids: string[];
            single_check_result?: SingleCheckResult | null;
            cross_check_result?: CrossCheckRunResult | null;
        }): Promise<AICrossAnalysisResult> => {
            return await apiPost<AICrossAnalysisResult>('/validation/ai-cross-analysis', {
                container_id: params.container_id,
                document_ids: params.document_ids,
                single_check_result: params.single_check_result ?? null,
                cross_check_result: params.cross_check_result ?? null,
            });
        },
    });
}
